import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, TextField,
  Button, Box, Alert, Grid, Select, MenuItem, Chip, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, CircularProgress
} from '@mui/material';
import { api } from '../services/api';
import { useNavigate } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import DarkBackground from '../components/DarkBackground';
import WarningIcon from '@mui/icons-material/Warning';
import GroupIcon from '@mui/icons-material/Group';


const predefinedZones = [
  // Gran Buenos Aires
  'GBA Norte',
  'GBA Sur',
  'GBA Oeste',
  'CABA',
  
  // Provinces
  'Provincia de Buenos Aires',
  'Provincia de Córdoba',
  'Provincia de Santa Fe',
  'Provincia de Mendoza',
  'Provincia de Tucumán',
  'Provincia de Entre Ríos',
  'Provincia de Salta',
  'Provincia de Misiones',
  'Provincia de Chaco',
  'Provincia de Corrientes',
  'Provincia de Santiago del Estero',
  'Provincia de San Juan',
  'Provincia de Jujuy',
  'Provincia de Río Negro',
  'Provincia de Neuquén',
  'Provincia de Formosa',
  'Provincia de Chubut',
  'Provincia de San Luis',
  'Provincia de Catamarca',
  'Provincia de La Rioja',
  'Provincia de La Pampa',
  'Provincia de Santa Cruz',
  'Provincia de Tierra del Fuego',

  // Important Regions
  'Costa Atlántica',
  'Cuyo',
  'NOA',
  'NEA',
  'Patagonia Norte',
  'Patagonia Sur'
];

const ProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const userType = localStorage.getItem('userType');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [originalProfile, setOriginalProfile] = useState(null);

  const fetchProfile = async () => {
    try {
      
      let response;
      if (userType === 'business') {
        response = await api.getBusinessProfile();
      } else if (userType === 'distributor') {
        response = await api.getDistributorProfile();
      } else if (userType === 'subUser') {
        response = await api.getSubUserProfile();
      } else {
        throw new Error('Invalid user type');
      }

      
      const profileData = response?.data || response;
      setProfile(profileData);
      setOriginalProfile(profileData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Error al cargar el perfil');
      navigate('/');
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [userType]); // Added userType as dependency

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setProfile(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setProfile(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleChangePasswordDialogOpen = () => {
    setChangePasswordDialogOpen(true);
  }

  const handleChangePasswordDialogClose = () => {
    setChangePasswordDialogOpen(false);
  }

  const handleChangePassword = async (e) => {
    e.preventDefault();
    await api.changePassword(profile.password, profile.newPassword);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Only send fields that have changed from their original values
      const changedFields = {};
      Object.keys(profile).forEach(key => {
        if (profile[key] !== originalProfile[key]) {
          changedFields[key] = profile[key];
        }
      });

      if (userType === 'business') {
        await api.updateBusinessProfile(changedFields);
      } else {
        await api.updateDistributorProfile(profile._id, changedFields);
      }
      setSuccess('Perfil actualizado exitosamente');
      setError('');
    } catch (error) {
      setError(error.response?.data?.message || 'Error al actualizar el perfil');
      setSuccess('');
    }
  };

  const handleDeleteAccount = async () => {
    try {
      if (userType === 'business') {
        await api.deleteBusinessProfile();
      } else {
        await api.deleteDistributorProfile();
      }
      
      // Clear local storage and redirect to home
      localStorage.clear();
      navigate('/');
    } catch (error) {
      setError(error.response?.data?.message || 'Error al eliminar la cuenta');
    }
    setDeleteDialogOpen(false);
  };

  const handleProfileImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await api.uploadProfileImage(formData);
      
      setProfile(prev => ({
        ...prev,
        profileImage: response.url
      }));
      
      setSuccess('Imagen de perfil actualizada exitosamente');
    } catch (error) {
      console.error('Error uploading profile image:', error);
      setError('Error al subir la imagen. Por favor, inténtelo de nuevo.');
    } finally {
      setUploadingImage(false);
    }
  };
  
  if (loading) return <LoadingScreen message="Cargando perfil..." />;
  
  if (changePasswordDialogOpen) return <ChangePasswordDialog open={changePasswordDialogOpen} onClose={handleChangePasswordDialogClose} />;
  return (
    <DarkBackground>
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center',justifyContent: 'space-between', gap: 2 }}>  
        <Typography variant="h4" gutterBottom>
          Mi perfil
        </Typography>
        {userType === 'distributor' && (
          <Box sx={{ mt: 3, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<GroupIcon />}
              onClick={() => navigate('/sub-users')}
              sx={{ 
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  backgroundColor: 'primary.50'
                }
              }}
            >
              Gestionar Sub-usuarios
            </Button>
          </Box>
        )}
        </Box>


        {userType === 'distributor' && profile?.subscription && (
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              mb: 3, 
              display: 'flex', 
              alignItems: 'center',
              bgcolor: profile.subscription.status === 'pending' ? '#fff3e0' :
                      profile.subscription.planId === 'plata' ? '#f5f5f5' :
                      profile.subscription.planId === 'oro' ? '#fff9c4' :
                      profile.subscription.planId === 'diamante' ? '#e0f7fa' : '#fff3e0'
            }}
          >
            <WorkspacePremiumIcon 
              sx={{ 
                fontSize: 40, 
                mr: 2,
                color: profile.subscription.status === 'pending' ? '#ffa726' :
                       profile.subscription.planId === 'plata' ? '#C0C0C0' :
                       profile.subscription.planId === 'oro' ? '#FFD700' :
                       profile.subscription.planId === 'diamante' ? '#00FFFF' : '#FF8C00'
              }} 
            />
            <Box>
              <Typography variant="h6">
                Plan {profile.subscription.planId.charAt(0).toUpperCase() + profile.subscription.planId.slice(1)}
                {profile.subscription.status === 'pending' && ' (PENDIENTE)'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {profile.subscription.status === 'pending' ? 
                  'Esperando confirmación de pago' :
                  `Vence el ${new Date(profile.subscription.endDate).toLocaleDateString()}`
                }
              </Typography>
            </Box>
            <Button 
              variant="outlined" 
              sx={{ ml: 'auto' }}
              onClick={() => navigate('/pricing')}
            >
              Gestionar Plan
            </Button>
          </Paper>
        )}

        {!profile.emailVerified && (
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              mb: 3, 
              display: 'flex', 
              alignItems: 'center',
              bgcolor: '#fff3e0',
              gap: 2
            }}
          >
            <WarningIcon sx={{ color: '#f57c00' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" color="warning.dark">
                Email no verificado
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Por favor, verifica tu email para acceder a todas las funcionalidades.
              </Typography>
            </Box>
            <Button 
              variant="contained" 
              color="warning"
              onClick={async () => {
                try {
                  await api.resendVerificationEmail();
                  setSuccess('Email de verificación enviado. Por favor, revisa tu bandeja de entrada.');
                } catch (error) {
                  setError('Error al enviar el email de verificación.');
                }
              }}
            >
              Reenviar Email
            </Button>
          </Paper>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              overflow: 'hidden',
              position: 'relative',
              bgcolor: 'grey.200',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {profile.profileImage ? (
              <img
                src={profile.profileImage}
                alt="Profile"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            ) : (
              <Typography variant="h3" color="text.secondary">
                {profile.name?.charAt(0)?.toUpperCase()}
              </Typography>
            )}
            <Button
              component="label"
              disabled={uploadingImage}
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.7)'
                },
                minWidth: 'unset',
                py: 0.5
              }}
            >
              {uploadingImage ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CloudUploadIcon fontSize="small" />
              )}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleProfileImageUpload}
              />
            </Button>
          </Box>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre"
                name="name"
                value={profile.name}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={userType === 'business' ? 'Razón Social' : 'Nombre Legal'}
                name={userType === 'business' ? 'razonSocial' : 'governmentName'}
                value={userType === 'business' ? profile.razonSocial : profile.governmentName}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Teléfono"
                name="phone"
                value={profile.phone}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Dirección"
                name="address"
                value={profile.address}
                onChange={handleChange}
              />
            </Grid>

            {userType === 'distributor' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Descripción"
                    name="description"
                    value={profile.description}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Zonas de Distribución
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel>Agregar Zona</InputLabel>
                    <Select
                      value=""
                      label="Agregar Zona"
                      onChange={(e) => {
                        const newZone = e.target.value;
                        if (newZone && (!profile.zones || !profile.zones.includes(newZone))) {
                          const newZones = [...(profile.zones || []), newZone];
                          setProfile(prev => ({ ...prev, zones: newZones }));
                        }
                      }}
                    >
                      {predefinedZones
                        .filter(zone => !profile.zones?.includes(zone))
                        .map((zone) => (
                          <MenuItem key={zone} value={zone}>
                            {zone}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    {profile.zones?.map((zone, index) => (
                      <Chip
                        key={index}
                        label={zone}
                        onDelete={() => {
                          const newZones = profile.zones.filter((_, i) => i !== index);
                          setProfile(prev => ({ ...prev, zones: newZones }));
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              </>
            )}
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            sx={{ mt: 3 }}
          >
            Guardar Cambios
          </Button>
          <Button
            variant="outlined"
            onClick={() => setChangePasswordDialogOpen(true)}
            sx={{ mt: 3 }}
          >
            Cambiar Contraseña
          </Button>
          </Box>
        </Box>

        <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Zona Peligrosa
          </Typography>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
          >
            Eliminar Cuenta
          </Button>
        </Box>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>¿Estás seguro?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Esta acción eliminará permanentemente tu cuenta y todos los datos asociados. 
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            color="primary"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
          >
            Eliminar Cuenta
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
    </DarkBackground>
  );
};

export default ProfilePage; 